import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";

// Import React Router
import { createBrowserRouter, RouterProvider } from "react-router-dom";

// Selective Bootstrap imports + custom styles (replaces full bootstrap.min.css)
import "./index.scss";
// Bootstrap JS not imported - navbar toggle is React-controlled

// Root is eagerly imported — it is a thin shell (navbar + outlet + footer)
// required on every route. Wrapping it in React.lazy caused a hydration
// mismatch (Error #418): hydrateRoot saw the pre-rendered full page in the
// DOM but React wanted to show the Suspense fallback spinner while the Root
// chunk resolved, forcing a full client-side re-render and pushing LCP to ~4s.
import Root from "./routes/root";

// Page-level routes are ALSO eagerly imported — for the same reason as Root.
// react-snap serialises the live DOM, which strips React's Suspense
// dehydration markers (<!--$-->/<!--/$-->). Without those markers, hydrateRoot
// cannot tell that the fully pre-rendered page content belongs to a suspended
// (lazy, not-yet-loaded) boundary, so it tries to render the PageLoader spinner
// instead — a mismatch against the baked-in HTML — and throws Error #418, then
// discards and re-renders the whole page on the client (defeating pre-render).
// Selective Hydration only resolves this for streaming SSR, which emits those
// markers; react-snap does not. The pages are small, so bundling them into
// main.js is a worthwhile trade for correct, flash-free hydration.
import HomePage from "./routes/home-page";
import AboutPage from "./routes/about-page";
import BlogPage from "./routes/blog-page";
import PricePage from "./routes/prices-page";
import HowToBook from "./routes/howtobook-page";
import SamplesPage from "./routes/samples-page";

// ErrorPage stays lazy: it only renders on a routing error, is never
// pre-rendered, and therefore never hydrates against baked-in HTML.
const ErrorPage = React.lazy(() => import("./routes/error-page"));

// Loading component for better UX during code splits
const PageLoader = () => (
  <div
    className="d-flex justify-content-center align-items-center"
    style={{ minHeight: "200px" }}
  >
    <div
      className="spinner-border text-primary"
      role="status"
      aria-label="Loading page"
    >
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const router = createBrowserRouter([
  {
    path: "/",
    element: <Root />,
    errorElement: (
      <Suspense fallback={<PageLoader />}>
        <ErrorPage />
      </Suspense>
    ),
    children: [
      {
        element: <HomePage />,
        index: true,
      },
      {
        path: "/AboutUs.html",
        element: <AboutPage />,
      },
      {
        path: "/about.html",
        element: <AboutPage />,
      },
      {
        path: "/about",
        element: <AboutPage />,
      },
      {
        path: "/blog",
        element: <BlogPage />,
      },
      {
        path: "/Blog.html",
        element: <BlogPage />,
      },
      {
        path: "/samples",
        element: <SamplesPage />,
      },
      {
        path: "/prices",
        element: <PricePage />,
      },
      {
        path: "/prices.html",
        element: <PricePage />,
      },
      {
        path: "/howtobook.html",
        element: <HowToBook />,
      },
      {
        path: "/howtobook",
        element: <HowToBook />,
      },
    ],
  },
]);

const container = document.getElementById("root");
const app = (
  <React.StrictMode>
    <HelmetProvider>
      <RouterProvider router={router} />
    </HelmetProvider>
  </React.StrictMode>
);

// Hydrate pre-rendered HTML from react-snap; fall back to createRoot for dev
if (container.hasChildNodes()) {
  ReactDOM.hydrateRoot(container, app);
} else {
  ReactDOM.createRoot(container).render(app);
}

import("./reportWebVitals").then((module) => {
  module.default((metric) => {
    if (process.env.NODE_ENV === "development") {
      console.log(metric);
    }
    if (window.gtag) {
      window.gtag("event", metric.name, {
        value: Math.round(metric.name === "CLS" ? metric.value * 1000 : metric.value),
        metric_id: metric.id,
        metric_value: metric.value,
        metric_delta: metric.delta,
        non_interaction: true,
      });
    }
  });
});

// Register service worker for offline caching and mobile performance
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/serviceWorker.js")
      .catch((err) => console.log("Service worker registration failed:", err));
  });
}
